import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Target, TrendingUp, Brain, Users, GraduationCap, Lightbulb, Users2, MonitorPlay } from 'lucide-react';

// Highlight component for important keywords
const Highlight = ({ children }) => (
  <span className="font-semibold text-[var(--brand-navy)]">
    {children}
  </span>
);
import { Button } from '@/components/ui/button';

const HomePage = () => {
  const features = [
    {
      icon: Users2,
      title: 'Expert Mentorship',
      description: 'Guidance from educators who’ve taught this exact syllabus — so you’re never guessing what to do next.'
    },
    {
      icon: BookOpen,
      title: 'Structured Study Resources',
      description: 'Crisp, well-organized notes, DPPs, and revision sheets — built to simplify tough concepts, not overwhelm you.'
    },
    {
      icon: Target,
      title: 'Daily Practice Problems',
      description: 'Curated DPPs that build problem-solving reflexes, one focused set at a time.'
    },
    {
      icon: MonitorPlay,
      title: 'Personalized Study Plans',
      description: 'Weekly and monthly plans built around your syllabus, pace, and exam goals — so you always know what to study next.'
    },
    {
      icon: TrendingUp,
      title: 'Regular Test Series',
      description: 'Realistic mock tests and topic-wise quizzes, with feedback that shows exactly where to improve.'
    },
    {
      icon: Brain,
      title: 'Clarity. Strategy. Support.',
      description: 'Mentorship isn’t just teaching — it’s helping you think like a ranker.'
    }
  ];

  const targetAudience = [
    {
      icon: Users,
      title: 'School Students',
      description: (
        <>
          Structured guidance and resources for school and <Highlight>Class 10 & 12</Highlight> board exam preparation.
        </>
      )
    },
    {
      icon: GraduationCap,
      title: 'Entrance Aspirants',
      description: (
        <>
          Targeted mentorship for <Highlight>JEE</Highlight>, <Highlight>NEET</Highlight>, and other <Highlight>entrance exams</Highlight> — with strategy, tests, and feedback.
        </>
      )
    },
    {
      icon: Lightbulb,
      title: 'Learners',
      description: 'For those who simply love learning Physics the right way — no pressure, just curiosity.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Physics Notebook — Clarity, Strategy, and Mentorship</title>
        <meta name="description" content="Structured notes, daily practice, and mentorship for Class 11/12, JEE, and NEET physics — built by educators who've taught this exact syllabus." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sm font-semibold tracking-[0.2em] text-[var(--brand-blue)] mb-5 uppercase"
            >
              For Boards &middot; JEE &middot; NEET
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-4xl md:text-6xl font-semibold text-gray-900 mb-6 leading-[1.1]"
            >
              Physics, without
              <span className="gradient-text block">the guesswork.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
            >
              Structured notes, daily practice, and mentorship from educators who’ve taught this exact syllabus — so every hour you put in moves the score, not just the clock. Built for <Highlight>Boards</Highlight>, <Highlight>JEE</Highlight>, and <Highlight>NEET</Highlight>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="/dlp"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Button className="btn-primary text-white px-8 py-3 text-lg">
                  See the Learning Plan
                </Button>
              </a>
              <a
                href="https://blog.physicsnotebook.in"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Button variant="outline" className="px-8 py-3 text-lg">
                  Read Our Blog
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              Everything that closes the gap between studying and scoring
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Because you don’t just need notes — you need guidance, structure, and accountability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'linear-gradient(115deg, var(--brand-navy), var(--brand-blue))' }}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              Built for Every Physics Learner
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From acing school exams to cracking competitive tests — the guidance changes, the standard doesn’t.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={audience.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg card-hover text-center"
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'linear-gradient(115deg, var(--brand-navy), var(--brand-blue))' }}>
                  <audience.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{audience.title}</h3>
                <p className="text-gray-600">
                  {audience.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding dark-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to stop guessing at physics?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join the students who’ve traded guesswork for a clear plan — and are already seeing it in their scores.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/solutions">
                <Button className="bg-white text-[var(--brand-navy)] hover:bg-gray-100 px-8 py-3 text-lg font-semibold">
                  Start Learning Now
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[var(--brand-navy)] px-8 py-3 text-lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
